import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { 
  CheckCircle, Package, Truck, Loader2, ArrowLeft, Store, MapPin, Phone, 
  Clock, RotateCcw, AlertTriangle, Info, ChevronDown, ChevronUp, CreditCard, XCircle,
  Calendar, Sparkles
} from 'lucide-react';
import { api } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentChecked, setPaymentChecked] = useState(false);
  const [resumingPayment, setResumingPayment] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  
  // Return request state
  const [returnEligibility, setReturnEligibility] = useState(null);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});
  const [returnReason, setReturnReason] = useState('');
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const [existingReturn, setExistingReturn] = useState(null);
  const [completedReturn, setCompletedReturn] = useState(null);
  const [showReturnDetails, setShowReturnDetails] = useState(false);

  const sessionId = searchParams.get('session_id');
  const paymentCancelled = searchParams.get('payment_cancelled');

  useEffect(() => {
    const checkPaymentAndFetch = async () => {
      if (sessionId && !paymentChecked) {
        try {
          const maxAttempts = 5;
          for (let i = 0; i < maxAttempts; i++) {
            const res = await api.get(`/payments/status/${sessionId}`);
            if (res.data.payment_status === 'paid') {
              clearCart();
              setPaymentChecked(true);
              break;
            }
            await new Promise(r => setTimeout(r, 2000));
          }
        } catch (error) {
          console.error('Payment check error:', error);
        }
      }

      try {
        const res = await api.get(`/orders/${orderId}`);
        setOrderData(res.data);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    checkPaymentAndFetch();
  }, [orderId, sessionId, clearCart, paymentChecked]);

  // Check return eligibility when order is delivered OR for EXPERIENCE orders (can cancel before event)
  useEffect(() => {
    const checkReturnEligibility = async () => {
      // Per EXPERIENCE: possiamo mostrare la policy anche prima della "consegna"
      // perché la cancellazione si basa sulla data evento
      const isExperience = orderData?.order?.has_experience;
      const isDelivered = orderData?.order?.status === 'consegnato';
      const canCheckEligibility = isDelivered || (isExperience && ['paid', 'in_preparazione'].includes(orderData?.order?.status));
      
      if (canCheckEligibility) {
        try {
          const res = await api.get(`/returns/eligibility/${orderId}`);
          setReturnEligibility(res.data);

          if (!res.data.eligible && res.data.existing_return_id) {
            // Fetch active return details (blocks new returns)
            const returnRes = await api.get(`/returns/${res.data.existing_return_id}`);
            setExistingReturn(returnRes.data);
          }

          if (res.data.completed_return_id) {
            // Fetch completed return details (non-blocking, shown alongside new eligibility)
            const completedRes = await api.get(`/returns/${res.data.completed_return_id}`);
            setCompletedReturn(completedRes.data);
          }
        } catch (error) {
          console.error('Error checking return eligibility:', error);
        }
      }
    };
    
    checkReturnEligibility();
  }, [orderData, orderId]);

  // Handle resume payment
  const handleResumePayment = async () => {
    setResumingPayment(true);
    try {
      const res = await api.post(`/orders/${orderId}/resume-payment`, {
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la ripresa del pagamento');
      setResumingPayment(false);
    }
  };

  // Handle cancel order
  const handleCancelOrder = async () => {
    setCancellingOrder(true);
    try {
      await api.post(`/orders/${orderId}/cancel`);
      toast.success('Ordine annullato con successo');
      
      // Refresh order data
      const res = await api.get(`/orders/${orderId}`);
      setOrderData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'annullamento');
    } finally {
      setCancellingOrder(false);
    }
  };

  const handleItemSelect = (productId, checked) => {
    setSelectedItems(prev => ({
      ...prev,
      [productId]: checked
    }));
  };

  const handleSubmitReturn = async () => {
    const itemsToReturn = Object.entries(selectedItems)
      .filter(([_, checked]) => checked)
      .map(([productId, _]) => {
        const item = returnEligibility.items.find(i => i.product_id === productId);
        return {
          product_id: productId,
          quantity: item?.quantity || 1
        };
      });
    
    if (itemsToReturn.length === 0) {
      toast.error('Seleziona almeno un prodotto da restituire');
      return;
    }
    
    setSubmittingReturn(true);
    try {
      const res = await api.post('/returns', {
        order_id: orderId,
        items: itemsToReturn,
        reason: returnReason || null
      });
      
      toast.success(res.data.message);
      setReturnDialogOpen(false);
      setExistingReturn(res.data.return_request);
      setReturnEligibility(prev => ({ ...prev, eligible: false }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la richiesta');
    } finally {
      setSubmittingReturn(false);
    }
  };

  const getReturnStatusBadge = (status) => {
    const styles = {
      return_pending_approval: 'bg-orange-100 text-orange-800',
      return_requested: 'bg-yellow-100 text-yellow-800',
      return_approved: 'bg-green-100 text-green-800',
      return_rejected: 'bg-red-100 text-red-800',
      return_in_transit: 'bg-blue-100 text-blue-800',
      return_received: 'bg-purple-100 text-purple-800',
      return_completed: 'bg-green-100 text-green-800',
      return_cancelled: 'bg-gray-100 text-gray-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getReturnStatusLabel = (status) => {
    const labels = {
      return_pending_approval: 'In attesa di approvazione',
      return_requested: 'In attesa di risposta',
      return_approved: 'Approvato',
      return_rejected: 'Rifiutato',
      return_in_transit: 'In transito',
      return_received: 'Ricevuto dal venditore',
      return_completed: 'Completato',
      return_cancelled: 'Annullato'
    };
    return labels[status] || status;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const getStatusStep = (status) => {
    const steps = ['pending', 'paid', 'in_preparazione', 'spedito', 'in_consegna', 'consegnato'];
    return steps.indexOf(status);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      payment_failed: 'bg-red-100 text-red-800',
      paid: 'bg-green-100 text-green-800',
      in_preparazione: 'bg-blue-100 text-blue-800',
      spedito: 'bg-purple-100 text-purple-800',
      in_consegna: 'bg-indigo-100 text-indigo-800',
      consegnato: 'bg-green-100 text-green-800',
      problema_spedizione: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'In attesa di pagamento',
      payment_failed: 'Pagamento fallito',
      paid: 'Pagato',
      in_preparazione: 'In preparazione',
      spedito: 'Spedito',
      in_consegna: 'In consegna',
      consegnato: 'Consegnato',
      problema_spedizione: 'Problema spedizione',
      cancelled: 'Annullato'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-text-secondary">Verifica del pagamento in corso...</p>
        </div>
      </div>
    );
  }

  if (!orderData || !orderData.order) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Ordine non trovato</h1>
          <Button asChild>
            <Link to="/ordini">Torna agli ordini</Link>
          </Button>
        </div>
      </div>
    );
  }

  const { order, items, shipment } = orderData;
  const currentStep = getStatusStep(order.status);
  
  const statusSteps = [
    { key: 'paid', label: 'Pagato', icon: CheckCircle },
    { key: 'in_preparazione', label: 'In Preparazione', icon: Package },
    { key: 'spedito', label: 'Spedito', icon: Truck },
    { key: 'consegnato', label: 'Consegnato', icon: CheckCircle }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="order-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/ordini" className="inline-flex items-center gap-2 text-text-secondary hover:text-primary mb-6">
          <ArrowLeft className="w-4 h-4" />
          Torna agli ordini
        </Link>

        {/* Payment Cancelled Banner */}
        {paymentCancelled && (order.status === 'pending' || order.status === 'payment_failed') && (
          <div className="mb-8 bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-yellow-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h2 className="text-lg font-bold text-yellow-800 mb-2">Pagamento Non Completato</h2>
                <p className="text-yellow-700 mb-4">
                  Il pagamento è stato annullato o non completato. Puoi riprovare quando vuoi.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pending/Payment Failed - Resume Payment Section */}
        {(order.status === 'pending' || order.status === 'payment_failed') && (
          <div className="mb-8 bg-orange-50 border border-orange-200 rounded-xl p-6" data-testid="resume-payment-section">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <CreditCard className="w-10 h-10 text-orange-600 flex-shrink-0" />
                <div>
                  <h2 className="text-lg font-bold text-orange-800">
                    {order.status === 'payment_failed' ? 'Pagamento Fallito' : 'In Attesa di Pagamento'}
                  </h2>
                  <p className="text-orange-700 text-sm">
                    {order.status === 'payment_failed' 
                      ? 'Si è verificato un problema con il pagamento. Puoi riprovare.'
                      : 'Completa il pagamento per confermare il tuo ordine.'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button 
                  onClick={handleResumePayment}
                  disabled={resumingPayment}
                  className="flex-1 sm:flex-none gap-2"
                  data-testid="resume-payment-btn"
                >
                  {resumingPayment ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Caricamento...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      {order.status === 'payment_failed' ? 'Riprova Pagamento' : 'Completa Pagamento'}
                    </>
                  )}
                </Button>
                
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="gap-2" data-testid="cancel-order-btn">
                      <XCircle className="w-4 h-4" />
                      Annulla
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Annullare l'ordine?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Sei sicuro di voler annullare questo ordine? L'azione non può essere annullata.
                        Dovrai creare un nuovo ordine se vorrai acquistare questi prodotti.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>No, torna indietro</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleCancelOrder}
                        disabled={cancellingOrder}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {cancellingOrder ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Annullamento...
                          </>
                        ) : (
                          'Sì, annulla ordine'
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            
            {/* Stock warning */}
            <div className="mt-4 p-3 bg-orange-100 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-orange-700 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-orange-800">
                  <strong>Nota:</strong> La disponibilità dei prodotti non è garantita fino al completamento del pagamento. 
                  Ti consigliamo di completare l'acquisto il prima possibile.
                </p>
              </div>
            </div>
          </div>
        )}

        {sessionId && order.status === 'paid' && (
          <div className="mb-8 bg-secondary/10 border border-secondary/20 rounded-xl p-6 text-center">
            <CheckCircle className="w-12 h-12 text-secondary mx-auto mb-3" />
            <h2 className="text-xl font-bold text-secondary mb-2">Pagamento Completato!</h2>
            <p className="text-text-secondary">Grazie per il tuo ordine. Ti invieremo un'email di conferma.</p>
          </div>
        )}

        <div className="bg-white rounded-xl border border-border p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-2xl font-bold">{order.order_number}</h1>
              <p className="text-text-secondary">
                {new Date(order.created_at).toLocaleDateString('it-IT', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              
              {/* Badge stato */}
              <Badge className={`mt-2 ${getStatusBadge(order.status)}`}>
                {order.status_display?.label || getStatusLabel(order.status)}
              </Badge>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-primary">{formatPrice(order.total)}</p>
            </div>
          </div>

          {/* Attribution - Chi vende e spedisce */}
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-sm">
              <Store className="w-4 h-4 text-primary" />
              <span className="font-medium">{order.attribution}</span>
            </div>
            <p className="text-xs text-text-muted mt-1">{order.platform_note}</p>
          </div>

          {/* Status Progress */}
          {order.status !== 'cancelled' && order.status !== 'pending' && (
            <div className="mb-6">
              <div className="flex justify-between">
                {statusSteps.map((step, idx) => {
                  const stepIndex = getStatusStep(step.key);
                  const isActive = currentStep >= stepIndex;
                  const Icon = step.icon;
                  return (
                    <div key={step.key} className="flex flex-col items-center flex-1">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        isActive ? 'bg-secondary text-white' : 'bg-gray-200 text-gray-400'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className={`text-xs mt-2 text-center ${isActive ? 'text-secondary font-medium' : 'text-text-muted'}`}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Status description */}
          {order.status_display?.description && (
            <div className="text-center py-3 bg-muted/30 rounded-lg mb-6">
              <p className="text-sm text-text-secondary">{order.status_display.description}</p>
            </div>
          )}

          {/* Tracking info */}
          {order.tracking_number && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Tracking Spedizione
                  </p>
                  <p className="font-mono text-lg font-bold text-blue-800 mt-1">
                    {order.tracking_number}
                  </p>
                </div>
                {shipment?.shipped_at && (
                  <div className="text-right">
                    <p className="text-xs text-blue-600">Spedito il</p>
                    <p className="text-sm font-medium text-blue-800">
                      {new Date(shipment.shipped_at).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Items */}
        <div className="bg-white rounded-xl border border-border p-6 mb-6">
          <h2 className="font-heading font-bold mb-4">Articoli Ordinati</h2>
          <div className="space-y-4">
            {items.map(item => (
              <div key={item.id} className={`flex gap-4 ${item.is_experience ? 'p-3 bg-purple-50 rounded-lg border border-purple-100' : ''}`}>
                <img
                  src={item.product_image || 'https://images.pexels.com/photos/7663192/pexels-photo-7663192.jpeg'}
                  alt={item.product_name}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{item.product_name}</h4>
                    {item.is_experience && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-200 text-purple-800">
                        <Sparkles className="w-3 h-3" />
                        Esperienza
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-text-secondary">
                    {formatPrice(item.unit_price)} x {item.quantity}
                  </p>
                  {item.producer_name && (
                    <p className="text-xs text-text-muted flex items-center gap-1 mt-1">
                      <Store className="w-3 h-3" />
                      {item.producer_name}
                    </p>
                  )}
                  {/* Dettagli EXPERIENCE */}
                  {item.is_experience && (item.event_date || item.location) && (
                    <div className="mt-2 pt-2 border-t border-purple-200 space-y-1">
                      {item.event_date && (
                        <p className="text-sm text-purple-700 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span className="font-medium">Data evento:</span>{' '}
                          {new Date(item.event_date).toLocaleDateString('it-IT', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                      {item.location && (
                        <p className="text-sm text-purple-700 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span className="font-medium">Luogo:</span> {item.location}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <p className="font-medium">{formatPrice(item.total_price)}</p>
              </div>
            ))}
          </div>

          {/* Order summary */}
          <div className="border-t border-border mt-4 pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-text-muted">Subtotale</span>
              <span>{formatPrice(order.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-text-muted">Spedizione</span>
              <span>{order.shipping_cost > 0 ? formatPrice(order.shipping_cost) : 'Gratuita'}</span>
            </div>
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-border">
              <span>Totale</span>
              <span className="text-primary">{formatPrice(order.total)}</span>
            </div>
          </div>
        </div>

        {/* Shipping address - Hide if order has ONLY experiences */}
        {!order.has_experience || items.some(item => !item.is_experience) ? (
          <div className="bg-white rounded-xl border border-border p-6 mb-6">
            <h2 className="font-heading font-bold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Indirizzo di Spedizione
            </h2>
            <div className="text-text-secondary">
              <p className="font-medium text-text-primary">{order.shipping_name}</p>
              <p>{order.shipping_address}</p>
              <p>{order.shipping_postal_code} {order.shipping_city}</p>
              <p>{order.shipping_country}</p>
              {order.shipping_phone && (
                <p className="flex items-center gap-1 mt-2 text-sm">
                  <Phone className="w-3 h-3" />
                  {order.shipping_phone}
                </p>
              )}
            </div>
          </div>
        ) : (
          /* Message for EXPERIENCE-only orders */
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-6">
            <h2 className="font-heading font-bold mb-2 flex items-center gap-2 text-purple-800">
              <Sparkles className="w-5 h-5" />
              Esperienza Acquistata
            </h2>
            <p className="text-purple-700 text-sm">
              Questa è un'esperienza e non richiede spedizione. I dettagli dell'evento sono riportati sopra. 
              Il venditore potrebbe contattarti per fornire ulteriori istruzioni.
            </p>
            {order.shipping_phone && (
              <p className="flex items-center gap-1 mt-3 text-sm text-purple-700">
                <Phone className="w-4 h-4" />
                Telefono di contatto: {order.shipping_phone}
              </p>
            )}
          </div>
        )}

        {/* Return Section - For delivered orders OR EXPERIENCE orders (can cancel before event) */}
        {(order.status === 'consegnato' || (order.has_experience && ['paid', 'in_preparazione'].includes(order.status))) && (
          <div className="bg-white rounded-xl border border-border p-6" data-testid="return-section">
            <h2 className="font-heading font-bold mb-4 flex items-center gap-2">
              <RotateCcw className="w-5 h-5" />
              {order.has_experience && items.every(i => i.is_experience) ? 'Cancellazione Esperienza' : 'Diritto di Recesso'}
            </h2>

            {/* Existing return request */}
            {existingReturn && (
              <div className="mb-4">
                <div 
                  className="bg-muted/50 rounded-lg p-4 cursor-pointer"
                  onClick={() => setShowReturnDetails(!showReturnDetails)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge className={getReturnStatusBadge(existingReturn.status)}>
                        {getReturnStatusLabel(existingReturn.status)}
                      </Badge>
                      <span className="text-sm text-text-secondary">
                        Richiesta del {new Date(existingReturn.created_at).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                    {showReturnDetails ? (
                      <ChevronUp className="w-5 h-5 text-text-muted" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-text-muted" />
                    )}
                  </div>
                  
                  {showReturnDetails && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3">
                      {/* Items */}
                      <div>
                        <p className="text-xs font-medium text-text-muted mb-2">Prodotti richiesti:</p>
                        <ul className="text-sm space-y-1">
                          {existingReturn.items?.map((item, idx) => (
                            <li key={idx} className="flex justify-between">
                              <span>{item.product_name}</span>
                              <span className="text-text-muted">x{item.quantity}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {existingReturn.reason && (
                        <div>
                          <p className="text-xs font-medium text-text-muted mb-1">Motivo:</p>
                          <p className="text-sm">{existingReturn.reason}</p>
                        </div>
                      )}
                      
                      {/* Vendor response */}
                      {existingReturn.vendor_response && (
                        <div className={`p-3 rounded-lg ${
                          existingReturn.status === 'return_approved' 
                            ? 'bg-green-50 border border-green-200' 
                            : existingReturn.status === 'return_rejected'
                            ? 'bg-red-50 border border-red-200'
                            : 'bg-blue-50 border border-blue-200'
                        }`}>
                          <p className="text-xs font-medium mb-1">
                            Risposta del venditore ({existingReturn.producer_name}):
                          </p>
                          <p className="text-sm">{existingReturn.vendor_response}</p>
                        </div>
                      )}
                      
                      {/* Return tracking */}
                      {existingReturn.return_tracking_number && (
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <p className="text-xs font-medium text-blue-700 mb-1">Tracking Reso:</p>
                          <p className="font-mono text-sm font-bold text-blue-800">
                            {existingReturn.return_tracking_number}
                          </p>
                          {existingReturn.return_carrier && (
                            <p className="text-xs text-blue-600">{existingReturn.return_carrier}</p>
                          )}
                        </div>
                      )}
                      
                      {/* Refund info */}
                      {existingReturn.refund_amount && (
                        <div className="bg-green-50 p-3 rounded-lg">
                          <p className="text-xs font-medium text-green-700 mb-1">Rimborso previsto:</p>
                          <p className="font-bold text-green-800">{formatPrice(existingReturn.refund_amount)}</p>
                          {existingReturn.refund_method && (
                            <p className="text-xs text-green-600">Metodo: {existingReturn.refund_method}</p>
                          )}
                          {existingReturn.refund_completed_at && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Completato il {new Date(existingReturn.refund_completed_at).toLocaleDateString('it-IT')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Completed return info (non-blocking — shown alongside new eligibility) */}
            {completedReturn && (
              <div className="mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className="bg-green-100 text-green-800">
                      {getReturnStatusLabel(completedReturn.status)}
                    </Badge>
                    <span className="text-sm text-text-secondary">
                      Reso del {new Date(completedReturn.created_at).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                  {completedReturn.items?.length > 0 && (
                    <ul className="text-sm space-y-0.5 mb-2">
                      {completedReturn.items.map((item, idx) => (
                        <li key={idx} className="flex justify-between text-green-800">
                          <span>{item.product_name}</span>
                          <span>x{item.quantity}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {completedReturn.refund_amount && (
                    <p className="text-sm font-medium text-green-700">
                      Rimborso: {formatPrice(completedReturn.refund_amount)}
                      {completedReturn.refund_status && completedReturn.refund_status !== 'skipped' && (
                        <span className="ml-2 text-xs">({completedReturn.refund_status})</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Return eligibility info - SUPPORTA ORDINI MISTI */}
            {returnEligibility && !existingReturn && (
              <>
                {/* ORDINI MISTI: Mostra tutte le policy */}
                {returnEligibility.is_mixed_order && returnEligibility.policies && (
                  <div className="space-y-4 mb-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-blue-800 text-sm">Ordine con prodotti misti</p>
                          <p className="text-xs text-blue-700 mt-1">
                            Questo ordine contiene prodotti di tipologie diverse con policy di reso differenti.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {returnEligibility.policies.map((policy, idx) => (
                      <div 
                        key={idx} 
                        className={`rounded-lg p-4 border ${
                          policy.eligible 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-gray-50 border-gray-200'
                        }`}
                        data-testid={`policy-${policy.macro_type}`}
                      >
                        <div className="flex items-start gap-3">
                          {policy.eligible ? (
                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          ) : (
                            <Clock className="w-5 h-5 text-gray-500 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                policy.macro_type === 'EXPERIENCE' ? 'bg-purple-200 text-purple-800' :
                                policy.macro_type === 'ARTIGIANATO' ? 'bg-amber-200 text-amber-800' :
                                'bg-green-200 text-green-800'
                              }`}>
                                {policy.macro_type === 'EXPERIENCE' ? 'Esperienza' : 
                                 policy.macro_type === 'ARTIGIANATO' ? 'Artigianato' : 'Alimentare'}
                              </span>
                              {policy.eligible && (
                                <Badge className="bg-green-100 text-green-800 text-xs">
                                  {policy.refund_percentage !== undefined 
                                    ? `Rimborso ${Math.round(policy.refund_percentage * 100)}%`
                                    : 'Idoneo'}
                                </Badge>
                              )}
                            </div>
                            
                            <p className={`text-sm font-medium ${policy.eligible ? 'text-green-800' : 'text-gray-700'}`}>
                              {policy.message || policy.reason}
                            </p>
                            
                            {/* Dettagli EXPERIENCE */}
                            {policy.macro_type === 'EXPERIENCE' && policy.experience_info && (
                              <div className="mt-2 text-sm text-purple-700">
                                <p className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Data evento: {new Date(policy.experience_info.event_date).toLocaleDateString('it-IT', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                  })}
                                </p>
                                <p className="text-xs mt-1">
                                  {policy.experience_info.days_to_event} giorni all'evento
                                </p>
                              </div>
                            )}
                            
                            {/* Dettagli ARTIGIANATO */}
                            {policy.macro_type === 'ARTIGIANATO' && policy.days_remaining !== undefined && (
                              <p className="text-xs text-amber-700 mt-1">
                                {policy.days_remaining} giorni rimanenti per il recesso
                              </p>
                            )}
                            
                            {/* Prodotti in questa categoria */}
                            {policy.items && policy.items.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-current/10">
                                <p className="text-xs text-text-muted mb-1">Prodotti:</p>
                                <ul className="text-xs space-y-0.5">
                                  {policy.items.map((item, itemIdx) => (
                                    <li key={itemIdx} className="flex justify-between">
                                      <span>{item.product_name}</span>
                                      <span className="text-text-muted">{formatPrice(item.total_price)}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Info policy */}
                            <p className="text-xs text-text-muted mt-2">
                              {policy.policy_info?.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ORDINI SINGOLO TIPO: Mostra la policy standard */}
                {!returnEligibility.is_mixed_order && returnEligibility.eligible && (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-green-800">
                            {returnEligibility.message}
                          </p>
                          {/* Info data evento per EXPERIENCE */}
                          {returnEligibility.experience_info && (
                            <p className="text-sm text-green-700 mt-1">
                              Data evento: {new Date(returnEligibility.experience_info.event_date).toLocaleDateString('it-IT', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          )}
                          {returnEligibility.deadline_date && returnEligibility.macro_type !== 'EXPERIENCE' && (
                            <p className="text-sm text-green-700 mt-1">
                              Data limite: {new Date(returnEligibility.deadline_date).toLocaleDateString('it-IT', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                ...(returnEligibility.macro_type === 'FOOD' ? { hour: '2-digit', minute: '2-digit' } : {})
                              })}
                            </p>
                          )}
                          {/* Mostra sempre la percentuale di rimborso per EXPERIENCE */}
                          {returnEligibility.macro_type === 'EXPERIENCE' && returnEligibility.refund_percentage !== undefined && (
                            <p className={`text-sm mt-1 font-medium ${
                              returnEligibility.refund_percentage >= 1 ? 'text-green-700' : 
                              returnEligibility.refund_percentage >= 0.5 ? 'text-yellow-700' : 'text-red-700'
                            }`}>
                              Rimborso previsto: {Math.round(returnEligibility.refund_percentage * 100)}%
                            </p>
                          )}
                          {/* Per altri tipi, mostra solo se < 100% */}
                          {returnEligibility.macro_type !== 'EXPERIENCE' && returnEligibility.refund_percentage !== undefined && returnEligibility.refund_percentage < 1 && (
                            <p className="text-sm text-yellow-700 mt-1">
                              Rimborso previsto: {Math.round(returnEligibility.refund_percentage * 100)}%
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-start gap-2 text-sm text-text-secondary">
                        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-text-primary mb-1">
                            {returnEligibility.macro_type === 'FOOD' && 'Policy Prodotti Alimentari'}
                            {returnEligibility.macro_type === 'ARTIGIANATO' && 'Diritto di Recesso UE'}
                            {returnEligibility.macro_type === 'EXPERIENCE' && 'Policy Esperienze'}
                            {!returnEligibility.macro_type && 'Policy di Reso'}
                          </p>
                          <p>
                            {returnEligibility.policy_info?.description || (
                              returnEligibility.macro_type === 'FOOD'
                                ? 'Ai sensi della normativa europea, hai diritto di recedere dall\'acquisto entro 14 giorni dalla consegna.'
                                : returnEligibility.macro_type === 'EXPERIENCE'
                                ? 'Cancellazione ≥7 giorni prima dell\'evento: 100%. 3-7 giorni: 50%. Meno di 3 giorni: 0%.'
                                : 'Ai sensi della normativa europea, hai diritto di recedere dall\'acquisto entro 14 giorni dalla consegna.'
                            )}
                          </p>
                          {returnEligibility.requires_manual_approval && (
                            <p className="mt-2 text-xs text-yellow-700 font-medium">
                              ⚠️ Questa richiesta richiede approvazione del venditore
                            </p>
                          )}
                          <p className="mt-2 text-xs">
                            <strong>Nota:</strong> Il reso è gestito direttamente dal venditore, non da DalSud. 
                            Il venditore ti fornirà le istruzioni per la restituzione.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Non idoneo al reso (ordine singolo tipo) */}
                {!returnEligibility.is_mixed_order && !returnEligibility.eligible && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-gray-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-700">
                          {returnEligibility.reason || "Non è possibile richiedere il reso per questo ordine"}
                        </p>
                        {returnEligibility.days_passed && (
                          <p className="text-sm text-gray-600 mt-1">
                            Sono trascorsi {returnEligibility.days_passed} giorni dalla consegna.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Bottone richiedi reso - solo se c'è almeno una policy idonea */}
                {returnEligibility.eligible && (
                  <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="gap-2 mt-4"
                        data-testid="request-return-btn"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Richiedi Reso
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Richiesta di Reso</DialogTitle>
                      </DialogHeader>
                      
                      <div className="space-y-4 py-4">
                        <div>
                          <Label className="text-sm font-medium">Seleziona i prodotti da restituire:</Label>
                          <div className="mt-2 space-y-2">
                            {returnEligibility.items?.map((item) => (
                              <label 
                                key={item.product_id}
                                className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/70"
                              >
                                <Checkbox
                                  checked={selectedItems[item.product_id] || false}
                                  onCheckedChange={(checked) => handleItemSelect(item.product_id, checked)}
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm">{item.product_name}</p>
                                    {item.macro_type && (
                                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                                        item.macro_type === 'EXPERIENCE' ? 'bg-purple-100 text-purple-700' :
                                        item.macro_type === 'ARTIGIANATO' ? 'bg-amber-100 text-amber-700' :
                                        'bg-green-100 text-green-700'
                                      }`}>
                                        {item.macro_type === 'EXPERIENCE' ? 'Esperienza' : 
                                         item.macro_type === 'ARTIGIANATO' ? 'Artigianato' : 'Food'}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-text-muted">
                                    {formatPrice(item.unit_price)} x {item.quantity}
                                  </p>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="return-reason" className="text-sm font-medium">
                            Motivo (facoltativo)
                          </Label>
                          <Textarea
                            id="return-reason"
                            value={returnReason}
                            onChange={(e) => setReturnReason(e.target.value)}
                            placeholder="Descrivi il motivo del reso (non obbligatorio per legge)"
                            className="mt-1"
                            rows={3}
                          />
                          <p className="text-xs text-text-muted mt-1">
                            Per la normativa UE non sei obbligato a fornire un motivo.
                          </p>
                        </div>
                        
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                            <p className="text-xs text-yellow-800">
                              La richiesta sarà inviata al venditore che ti contatterà con le istruzioni per la restituzione e il rimborso.
                            </p>
                          </div>
                        </div>
                        
                        <Button 
                          onClick={handleSubmitReturn}
                          disabled={submittingReturn || Object.values(selectedItems).filter(Boolean).length === 0}
                          className="w-full"
                          data-testid="confirm-return-btn"
                        >
                          {submittingReturn ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Invio in corso...
                            </>
                          ) : (
                            'Invia Richiesta di Reso'
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailPage;
